import { Palmtree } from 'lucide-react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type { PtoDate, StatHolidayDate } from '@/types/ui/resource-availability'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatDayLabel } from '@/lib/utils/availability-utils'

interface TimeOffCellBadgeStrings {
  /** Tooltip heading, e.g. "Time off". */
  heading?: string
  /** PTO section label, e.g. "PTO". */
  ptoLabel?: string
  /** Stat-holiday section label, e.g. "Stat holiday". */
  statLabel?: string
  /** Suffix appended to a half-day PTO entry, e.g. "½ day". */
  halfDaySuffix?: string
  /** Accessible label / native title on the icon, e.g. "Time off in this period". */
  iconAria?: string
}

interface TimeOffCellBadgeProps {
  ptoDates?: PtoDate[]
  statHolidayDates?: StatHolidayDate[]
  strings?: TimeOffCellBadgeStrings
}

/**
 * Small palm-tree badge for a heatmap cell that has PTO and/or stat-holiday days.
 * Hovering (or focusing) the icon opens a compact popover listing the exact days so a
 * planner can see what's eating into the worker's availability without leaving the grid.
 *
 * Renders nothing when there's no time off — callers can mount it unconditionally.
 */
export function TimeOffCellBadge({ ptoDates, statHolidayDates, strings }: TimeOffCellBadgeProps) {
  const pto = ptoDates ?? []
  const stat = statHolidayDates ?? []
  if (pto.length === 0 && stat.length === 0) return null

  const heading = strings?.heading || 'Time off'
  const ptoLabel = strings?.ptoLabel || 'PTO'
  const statLabel = strings?.statLabel || 'Stat holiday'
  const halfDaySuffix = strings?.halfDaySuffix || '½ day'
  const iconAria = strings?.iconAria || 'Time off in this period'

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={iconAria}
            title={iconAria}
            // The badge is purely informational — a click shouldn't dismiss the hover card or
            // leave a mouse-focus ring. Radix closes the tooltip from its own pointer-down /
            // click handlers, but composeEventHandlers skips those when ours calls
            // preventDefault. preventDefault on pointer-down also suppresses the mouse focus
            // ring (keyboard Tab still focuses → focus-visible ring below). stopPropagation
            // keeps the click off any cell handler.
            onPointerDown={(e) => e.preventDefault()}
            onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
            className="inline-flex items-center justify-center rounded-sm p-0.5 opacity-75 hover:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-current"
          >
            <Palmtree className="w-3 h-3" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        {/* Portal escapes the cell's stacking context so the popover isn't clipped by the
            scroll container or painted under adjacent rows. */}
        <TooltipPrimitive.Portal>
          <TooltipContent
            side="top"
            className="max-w-[220px] p-2 text-xs"
            // The content is a Radix DismissableLayer, and a pointer-down on the trigger
            // counts as "outside" it → onPointerDownOutside → dismiss. preventDefault stops
            // that so a click keeps the card up; hover-out still closes it normally.
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <div className="font-medium mb-1">{heading}</div>
            {pto.length > 0 && (
              <div className="mb-1 last:mb-0">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{ptoLabel}</div>
                <ul className="mt-0.5 space-y-0.5">
                  {pto.map(d => (
                    <li key={`pto-${d.date}`} className="tabular-nums">
                      {formatDayLabel(d.date)}
                      {d.isHalfDay && (
                        <span className="ml-1 text-muted-foreground">({halfDaySuffix})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {stat.length > 0 && (
              <div className="last:mb-0">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{statLabel}</div>
                <ul className="mt-0.5 space-y-0.5">
                  {stat.map(d => (
                    <li key={`stat-${d.date}`} className="tabular-nums">
                      {formatDayLabel(d.date)}
                      {d.name && <span className="ml-1 text-muted-foreground">— {d.name}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  )
}
